import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch } from 'react-icons/fi';
import articleService from '../../services/articleService';
import ArticleCard from './ArticleCard';
import SearchBar from './SearchBar';

const SearchContainer = styled.div`
  padding: 2rem 0;
  min-height: 70vh;
`;

const SearchHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .search-query {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: 2rem;
  }
`;

const SearchBarContainer = styled.div`
  max-width: 600px;
  margin: 0 auto 3rem;
`;

const SearchResults = styled.div`
  .results-count {
    margin-bottom: 2rem;
    color: ${props => props.theme.colors.textSecondary};
    font-size: 1rem;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 0 auto;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem 0;
  
  .no-results-icon {
    font-size: 4rem;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1rem;
  }
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    max-width: 500px;
    margin: 0 auto;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: ${props => props.theme.colors.textSecondary};
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${props => props.theme.colors.divider};
    border-top: 3px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');

  const query = searchParams.get('q') || '';

  useEffect(() => {
    if (query && query !== currentQuery) {
      setCurrentQuery(query);
      performSearch(query);
    }
  }, [query, currentQuery]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      const searchResults = await articleService.searchArticles(searchQuery);
      setResults(searchResults);
      setHasMore(searchResults.length >= 20); // Assume more results if we got 20
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    // In a real implementation, you would implement pagination
    // For now, we'll just disable the button
    setHasMore(false);
  };

  return (
    <SearchContainer>
      <div className="container">
        <SearchHeader>
          <h1>
            <FiSearch size={32} />
            Search Articles
          </h1>
          {query && (
            <div className="search-query">
              Results for: "<strong>{query}</strong>"
            </div>
          )}
        </SearchHeader>

        <SearchBarContainer>
          <SearchBar
            placeholder="Search for articles, topics, or authors..."
            onResultClick={(article) => {
              // Handle direct result click if needed
            }}
          />
        </SearchBarContainer>

        {loading ? (
          <LoadingState>
            <div className="loading-spinner"></div>
            <p>Searching articles...</p>
          </LoadingState>
        ) : query ? (
          <SearchResults>
            <div className="results-count">
              Found {results.length} article{results.length !== 1 ? 's' : ''}
            </div>

            {results.length > 0 ? (
              <>
                <ResultsGrid>
                  {results.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </ResultsGrid>

                {hasMore && (
                  <LoadMoreButton onClick={loadMore}>
                    Load More Results
                  </LoadMoreButton>
                )}
              </>
            ) : (
              <NoResults>
                <div className="no-results-icon">
                  <FiSearch />
                </div>
                <h3>No articles found</h3>
                <p>
                  We couldn't find any articles matching "{query}". 
                  Try different keywords or browse our sections to discover great content.
                </p>
              </NoResults>
            )}
          </SearchResults>
        ) : (
          <NoResults>
            <div className="no-results-icon">
              <FiSearch />
            </div>
            <h3>Start your search</h3>
            <p>
              Enter keywords above to search through our articles, or browse by section to discover content.
            </p>
          </NoResults>
        )}
      </div>
    </SearchContainer>
  );
};

export default SearchPage;